import { useMemo } from 'react';
import { useAppSelector } from '@store/index';
import {
  selectCadEnabled,
  selectDesignVerificationEnabled,
} from '@features/featureFlags/featureFlagsSlice';
import { NAV_FOOTER_SECTION, NAV_SECTIONS, NavSectionConfig } from '@constants/navigation';

interface NavSections {
  sections: NavSectionConfig[];
  footer: NavSectionConfig;
}

/**
 * `null` is the still-loading state: keep the item, or the rail jumps. The third
 * argument is optional so existing two-argument callers keep working.
 */
export const filterNavSections = (
  sections: NavSectionConfig[],
  cadEnabled: boolean | null | undefined,
  designVerificationEnabled?: boolean | null,
) =>
  sections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) =>
          (!item.requiresCad || cadEnabled !== false) &&
          (!item.requiresDesignVerification || designVerificationEnabled !== false),
      ),
    }))
    .filter((section) => section.items.length > 0);

export function useNavSections(): NavSections {
  const cadEnabled = useAppSelector(selectCadEnabled);
  const designVerificationEnabled = useAppSelector(selectDesignVerificationEnabled);

  // The footer runs through the same filter — same item type, same flags.
  return useMemo(
    () => ({
      sections: filterNavSections(NAV_SECTIONS, cadEnabled, designVerificationEnabled),
      footer:
        filterNavSections([NAV_FOOTER_SECTION], cadEnabled, designVerificationEnabled)[0] ??
        { ...NAV_FOOTER_SECTION, items: [] },
    }),
    [cadEnabled, designVerificationEnabled],
  );
}
